import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Send, Edit2, Trash2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommentsModal = ({ idea, onClose, onCommentAdded }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [idea.id]);

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/ideas/${idea.id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/ideas/${idea.id}/comments`, { content: newComment });
      setNewComment('');
      await fetchComments();
      onCommentAdded?.();
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (commentId) => {
    if (!editContent.trim()) return;

    try {
      await axios.put(`${API}/comments/${commentId}`, { content: editContent });
      setEditingComment(null);
      setEditContent('');
      await fetchComments();
      toast.success('Comment updated');
    } catch (error) {
      toast.error('Failed to update comment');
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      await axios.delete(`${API}/comments/${commentId}`);
      await fetchComments();
      onCommentAdded?.();
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const startEdit = (comment) => {
    setEditingComment(comment.id);
    setEditContent(comment.content);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-zinc-800 rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="comments-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center gap-3">
            <MessageCircle className="text-[#5B6B8F]" size={24} />
            <div>
              <h2 className="text-xl font-bold text-zinc-900 dark:text-white">Discussion</h2>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">{idea.title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="close-comments-modal"
            className="text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-lg p-2 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Comments List */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading ? (
            <div className="text-center py-8 text-zinc-500 dark:text-zinc-400">Loading comments...</div>
          ) : comments.length === 0 ? (
            <div className="text-center py-12">
              <MessageCircle className="mx-auto h-12 w-12 text-zinc-300 dark:text-zinc-600 mb-4" />
              <p className="text-zinc-600 dark:text-zinc-400 mb-2">No comments yet</p>
              <p className="text-sm text-zinc-500 dark:text-zinc-500">Start the conversation!</p>
            </div>
          ) : (
            comments.map((comment) => (
              <div
                key={comment.id}
                data-testid={`comment-${comment.id}`}
                className="bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-lg p-4"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold text-zinc-900 dark:text-white">{comment.user_name}</p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 font-mono">
                      {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                      {comment.updated_at !== comment.created_at && ' (edited)'}
                    </p>
                  </div>
                  {comment.user_id === user?.id && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => startEdit(comment)}
                        data-testid={`edit-comment-${comment.id}`}
                        className="text-zinc-400 dark:text-zinc-500 hover:text-[#5B6B8F] dark:hover:text-[#8BC34A] transition-colors"
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(comment.id)}
                        data-testid={`delete-comment-${comment.id}`}
                        className="text-zinc-400 dark:text-zinc-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>

                {editingComment === comment.id ? (
                  <div className="space-y-2">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      data-testid="edit-comment-textarea"
                      className="w-full min-h-[80px] rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#689F38] focus-visible:border-transparent resize-none"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(comment.id)}
                        data-testid="save-edit-button"
                        className="px-3 py-1.5 text-sm bg-[#5B6B8F] text-white hover:bg-[#4A5A7C] rounded-lg font-medium transition-all"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditingComment(null);
                          setEditContent('');
                        }}
                        data-testid="cancel-edit-button"
                        className="px-3 py-1.5 text-sm bg-zinc-200 dark:bg-zinc-700 text-zinc-900 dark:text-white hover:bg-zinc-300 dark:hover:bg-zinc-600 rounded-lg font-medium transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed whitespace-pre-wrap">
                    {comment.content}
                  </p>
                )}
              </div>
            ))
          )}
        </div>

        {/* Add Comment Form */}
        <div className="border-t border-zinc-200 dark:border-zinc-700 p-6">
          <form onSubmit={handleSubmit} className="space-y-3">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              data-testid="new-comment-textarea"
              className="w-full min-h-[100px] rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-4 py-3 text-sm text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#689F38] focus-visible:border-transparent resize-none"
            />
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!newComment.trim() || submitting}
                data-testid="submit-comment-button"
                className="flex items-center gap-2 bg-[#5B6B8F] text-white hover:bg-[#4A5A7C] shadow-sm rounded-lg px-4 py-2 font-medium transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={16} />
                {submitting ? 'Posting...' : 'Post Comment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CommentsModal;
